import { AnimatePresence, LazyMotion, m, type Variants } from 'framer-motion'
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react'
import { ArrowUpRight } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import { CalendarDots } from '@phosphor-icons/react/dist/ssr/CalendarDots'
import { Check } from '@phosphor-icons/react/dist/ssr/Check'
import { CopySimple } from '@phosphor-icons/react/dist/ssr/CopySimple'
import { Sparkle } from '@phosphor-icons/react/dist/ssr/Sparkle'
import { SeoHead } from '@/components/seo/SeoHead'
import { siteConfig } from '@/config/site'
import { copyContactEmail } from '@/lib/copyContactEmail'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import portraitBack80 from '@/assets/portrait-gosha-back-80.avif'
import portraitBack from '@/assets/portrait-gosha-back.avif'
import portraitFront120 from '@/assets/portrait-gosha-dog-120.avif'
import portraitFront180 from '@/assets/portrait-gosha-dog-180.avif'
import { HomeAiCaseStudies } from '@/components/home/HomeAiCaseStudies'
import { HomeSelectedWork } from '@/components/home/HomeSelectedWork'
import { HomePromptfreePromo } from '@/components/home/HomePromptfreePromo'

const HomeTestimonials = lazy(() =>
  import('@/components/home/HomeTestimonials').then((mod) => ({ default: mod.HomeTestimonials }))
)

const loadDomAnimation = () =>
  import('@/lib/motion-dom-animation').then((mod) => mod.domAnimation)

const promptfreeUrl = 'https://promptfr.ee'

const sectionRevealVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const scheduleIdle = (cb: () => void) => {
  if (typeof requestIdleCallback === 'function') {
    const id = requestIdleCallback(cb, { timeout: 2500 })
    return () => cancelIdleCallback(id)
  }
  const id = window.setTimeout(cb, 1)
  return () => window.clearTimeout(id)
}

export const HomePage = () => {
  const [temperatureC, setTemperatureC] = useState<number | null>(null)
  const [heroEmailCopied, setHeroEmailCopied] = useState(false)
  const [copyEmailToastVisible, setCopyEmailToastVisible] = useState(false)
  const [isAvatarHovered, setIsAvatarHovered] = useState(false)
  const [isAvatarSwapped, setIsAvatarSwapped] = useState(false)
  const [isPromptfreeLinkHovered, setIsPromptfreeLinkHovered] = useState(false)
  const [isScheduleLinkHovered, setIsScheduleLinkHovered] = useState(false)
  const prefersReducedMotion = usePrefersReducedMotion()
  const heroCopyResetTimerRef = useRef<number | null>(null)
  const copyToastResetTimerRef = useRef<number | null>(null)

  useEffect(() => {
    const controller = new AbortController()

    const loadTemperature = async () => {
      try {
        const response = await fetch(
          'https://api.open-meteo.com/v1/forecast?latitude=59.437&longitude=24.7536&current=temperature_2m&temperature_unit=celsius&timezone=auto',
          { signal: controller.signal }
        )

        if (!response.ok) {
          return
        }

        const data: { current?: { temperature_2m?: number } } = await response.json()
        const nextTemperature = data.current?.temperature_2m

        if (typeof nextTemperature === 'number') {
          setTemperatureC(Math.round(nextTemperature))
        }
      } catch {
        // Keep UI fallback text if request fails.
      }
    }

    const cancelIdle = scheduleIdle(() => void loadTemperature())
    return () => {
      cancelIdle()
      controller.abort()
    }
  }, [])

  useEffect(
    () => () => {
      for (const ref of [heroCopyResetTimerRef, copyToastResetTimerRef]) {
        if (ref.current !== null) {
          window.clearTimeout(ref.current)
        }
      }
    },
    []
  )

  const scheduleCopyUiReset = (
    timerRef: MutableRefObject<number | null>,
    setCopied: (value: boolean) => void
  ) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
    }
    timerRef.current = window.setTimeout(() => {
      setCopied(false)
      timerRef.current = null
    }, 1800)
  }

  const showCopyEmailToast = () => {
    setCopyEmailToastVisible(true)
    if (copyToastResetTimerRef.current !== null) {
      window.clearTimeout(copyToastResetTimerRef.current)
    }
    copyToastResetTimerRef.current = window.setTimeout(() => {
      setCopyEmailToastVisible(false)
      copyToastResetTimerRef.current = null
    }, 1800)
  }

  const handleHeroEmailCopy = async () => {
    const ok = await copyContactEmail()
    if (!ok) {
      return
    }
    setHeroEmailCopied(true)
    scheduleCopyUiReset(heroCopyResetTimerRef, setHeroEmailCopied)
    showCopyEmailToast()
  }

  return (
    <LazyMotion features={loadDomAnimation} strict>
      <>
        <SeoHead
          pathname="/"
          kind="home"
          lcpImageUrl={portraitFront120}
          lcpImageSrcSet={`${portraitFront120} 120w, ${portraitFront180} 180w`}
          lcpImageSizes="40px"
        />
        <div className="site-page md:pt-28">
          {/*
            Plain hero (no opacity-0 initial) so LCP text + portrait stay visible immediately.
            Below-fold blocks keep subtle reveal animations.
          */}
          <section className="mx-auto w-full max-w-[448px] px-4">
            <div className="space-y-6">
              <h1 className="ui-heading-balance [text-wrap:pretty] flex flex-col gap-1.5 font-['Familjen_Grotesk'] text-[28px] leading-[1.06] tracking-[-0.56px] text-[var(--foreground)]">
                <m.button
                  type="button"
                  aria-label="Swap portrait layers"
                  onClick={() => setIsAvatarSwapped((current) => !current)}
                  onMouseEnter={() => setIsAvatarHovered(true)}
                  onMouseLeave={() => setIsAvatarHovered(false)}
                  onFocus={() => setIsAvatarHovered(true)}
                  onBlur={() => setIsAvatarHovered(false)}
                  whileTap={{ scale: 0.96 }}
                  className="inline-flex h-[60px] w-10 cursor-pointer items-center justify-center self-start focus-visible:outline-none"
                >
                  <div className="relative h-[60px] w-10">
                    <m.img
                      alt="Gosha with his dog"
                      src={portraitFront120}
                      srcSet={`${portraitFront120} 120w, ${portraitFront180} 180w`}
                      sizes="40px"
                      width={40}
                      height={60}
                      fetchPriority="high"
                      animate={{
                        x: isAvatarSwapped ? 3 : 0,
                        y: isAvatarSwapped ? 3 : 0,
                        scale: isAvatarSwapped ? 0.94 : 1,
                        rotate: isAvatarSwapped
                          ? isAvatarHovered
                            ? 6
                            : 2
                          : isAvatarHovered
                            ? -6
                            : -1,
                      }}
                      transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
                      className="absolute inset-0 z-20 h-full w-full rounded-[6px] object-cover shadow-[0_7px_18px_rgba(0,0,0,0.14)]"
                    />
                    <m.img
                      alt="Portrait of Gosha alternate"
                      src={portraitBack80}
                      srcSet={`${portraitBack80} 80w, ${portraitBack} 120w`}
                      sizes="40px"
                      width={40}
                      height={60}
                      animate={{
                        x: isAvatarSwapped ? 0 : 3,
                        y: isAvatarSwapped ? 0 : 3,
                        scale: isAvatarSwapped ? 1 : 0.94,
                        rotate: isAvatarSwapped
                          ? isAvatarHovered
                            ? -6
                            : -1
                          : isAvatarHovered
                            ? 6
                            : 2,
                      }}
                      transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
                      className="absolute inset-0 h-full w-full rounded-[6px] object-cover shadow-[0_7px_18px_rgba(0,0,0,0.14)]"
                      style={{ zIndex: isAvatarSwapped ? 20 : 10 }}
                    />
                  </div>
                </m.button>
                <div className="flex flex-col gap-0">
                  <div>Gosha Knyazhev.</div>
                  <div>
                    Love to design from{' '}
                    <div className="inline-block text-[var(--foreground)]">
                      Tallinn
                      <sup className="ml-1.5 text-[0.72em] font-normal leading-none text-[var(--foreground-muted)] tabular-nums">
                        {temperatureC === null ? '...' : temperatureC}&deg;
                      </sup>
                    </div>
                  </div>
                </div>
              </h1>
              <div className="max-w-[448px] space-y-6 text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--foreground-muted)] [text-wrap:pretty]">
                <p>
                  I’m a <span className="font-medium text-[var(--foreground)]">product designer and builder</span>.
                  From interfaces and websites to tools that cut repetitive work. I also design{' '}
                  <span className="font-medium text-[var(--foreground)]">AI flows</span> for{' '}
                  <span className="font-medium text-[var(--foreground)]">hyperpersonalized web</span>. More about my work{' '}
                  <a
                    href={siteConfig.linkedInUrl}
                    rel="noreferrer"
                    target="_blank"
                    aria-label="Read more on LinkedIn (opens in new tab)"
                    className="text-[var(--foreground-muted)] underline decoration-[var(--foreground-muted)] underline-offset-3 [transition-property:color,text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:text-[var(--foreground)] hover:decoration-[var(--foreground)]"
                  >
                    on LinkedIn
                  </a>
                  .
                </p>
                <p>
                  I also built{' '}
                  <a
                    href={promptfreeUrl}
                    rel="noreferrer"
                    target="_blank"
                    onMouseEnter={() => setIsPromptfreeLinkHovered(true)}
                    onMouseLeave={() => setIsPromptfreeLinkHovered(false)}
                    onFocus={() => setIsPromptfreeLinkHovered(true)}
                    onBlur={() => setIsPromptfreeLinkHovered(false)}
                    aria-label="Open PromptFree (opens in new tab)"
                    className="inline-flex items-center gap-1 font-medium [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)]"
                    style={{
                      color: 'var(--foreground)',
                      textDecorationLine: 'underline',
                      textDecorationColor: isPromptfreeLinkHovered ? 'var(--foreground)' : '#b7b7b7',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    PromptFree
                    <span className="relative inline-flex size-4 items-center justify-center">
                      <m.span
                        aria-hidden
                        animate={{
                          opacity: isPromptfreeLinkHovered ? 0 : 1,
                          scale: isPromptfreeLinkHovered ? 0.25 : 1,
                          filter: isPromptfreeLinkHovered ? 'blur(4px)' : 'blur(0px)',
                        }}
                        transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <Sparkle aria-hidden size={16} weight="regular" />
                      </m.span>
                      <m.span
                        aria-hidden
                        animate={{
                          opacity: isPromptfreeLinkHovered ? 1 : 0,
                          scale: isPromptfreeLinkHovered ? 1 : 0.25,
                          filter: isPromptfreeLinkHovered ? 'blur(0px)' : 'blur(4px)',
                        }}
                        transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <ArrowUpRight aria-hidden size={16} weight="regular" />
                      </m.span>
                    </span>
                  </a>{' '}
                  &mdash; one-shot AI prompts for your next pet-project landing page, portfolio, or hobby website. Reach
                  me by{' '}
                  <button
                    type="button"
                    onClick={handleHeroEmailCopy}
                    className="group inline-flex items-center gap-1 font-medium text-[var(--foreground)] underline decoration-[#b7b7b7] underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-[var(--foreground)]"
                  >
                    email
                    <span className="relative inline-flex size-5 items-center justify-center">
                      <m.span
                        aria-hidden
                        animate={{
                          opacity: heroEmailCopied ? 0 : 1,
                          scale: heroEmailCopied ? 0.25 : 1,
                          filter: heroEmailCopied ? 'blur(4px)' : 'blur(0px)',
                        }}
                        transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                          <CopySimple size={16} weight="fill" />
                        </span>
                        <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-150 group-hover:opacity-0">
                          <CopySimple size={16} weight="regular" />
                        </span>
                      </m.span>
                      <m.span
                        aria-hidden
                        animate={{
                          opacity: heroEmailCopied ? 1 : 0,
                          scale: heroEmailCopied ? 1 : 0.25,
                          filter: heroEmailCopied ? 'blur(0px)' : 'blur(4px)',
                        }}
                        transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <Check size={16} weight="bold" />
                      </m.span>
                    </span>
                  </button>{' '}
                  or even{' '}
                  <a
                    href={siteConfig.calendlyUrl}
                    rel="noreferrer"
                    target="_blank"
                    onMouseEnter={() => setIsScheduleLinkHovered(true)}
                    onMouseLeave={() => setIsScheduleLinkHovered(false)}
                    onFocus={() => setIsScheduleLinkHovered(true)}
                    onBlur={() => setIsScheduleLinkHovered(false)}
                    aria-label="Schedule a call (opens in new tab)"
                    className="inline-flex items-center gap-1 font-medium [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)]"
                    style={{
                      color: 'var(--foreground)',
                      textDecorationLine: 'underline',
                      textDecorationColor: isScheduleLinkHovered ? 'var(--foreground)' : '#b7b7b7',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    schedule a call
                    <span className="relative inline-flex size-4 items-center justify-center">
                      <m.span
                        aria-hidden
                        animate={{
                          opacity: isScheduleLinkHovered ? 0 : 1,
                          scale: isScheduleLinkHovered ? 0.25 : 1,
                          filter: isScheduleLinkHovered ? 'blur(4px)' : 'blur(0px)',
                        }}
                        transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <CalendarDots aria-hidden size={16} weight="regular" />
                      </m.span>
                      <m.span
                        aria-hidden
                        animate={{
                          opacity: isScheduleLinkHovered ? 1 : 0,
                          scale: isScheduleLinkHovered ? 1 : 0.25,
                          filter: isScheduleLinkHovered ? 'blur(0px)' : 'blur(4px)',
                        }}
                        transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <ArrowUpRight aria-hidden size={16} weight="regular" />
                      </m.span>
                    </span>
                  </a>
                  . If you're in Tallinn, let's go for a dog walk or coffee.
                </p>
              </div>
            </div>
          </section>
          <m.div
            variants={sectionRevealVariants}
            initial={prefersReducedMotion ? false : 'hidden'}
            whileInView={prefersReducedMotion ? undefined : 'visible'}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.45, ease: [0.2, 0, 0, 1] }}
            className="pt-[64px]"
          >
            <HomeSelectedWork />
          </m.div>
          <m.div
            variants={sectionRevealVariants}
            initial={prefersReducedMotion ? false : 'hidden'}
            whileInView={prefersReducedMotion ? undefined : 'visible'}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.45, ease: [0.2, 0, 0, 1] }}
            className="pt-[100px]"
          >
            <HomeAiCaseStudies />
          </m.div>
          <m.div
            variants={sectionRevealVariants}
            initial={prefersReducedMotion ? false : 'hidden'}
            whileInView={prefersReducedMotion ? undefined : 'visible'}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.45, ease: [0.2, 0, 0, 1] }}
            className="pt-[100px]"
          >
            <HomePromptfreePromo />
          </m.div>
          <m.div
            variants={sectionRevealVariants}
            initial={prefersReducedMotion ? false : 'hidden'}
            whileInView={prefersReducedMotion ? undefined : 'visible'}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.45, ease: [0.2, 0, 0, 1] }}
            className="pt-[100px]"
          >
            <Suspense>
              <HomeTestimonials />
            </Suspense>
          </m.div>
        </div>
        <AnimatePresence>
          {copyEmailToastVisible ? (
            <m.div
              key="email-copied-toast"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
              className="pointer-events-none fixed inset-x-0 bottom-5 z-20 flex justify-center px-4"
            >
              <div className="rounded-[var(--radius-lg)] bg-[var(--primary)] px-3 py-2 text-[13px] font-medium tracking-[-0.13px] text-[var(--primary-foreground)] shadow-[0_8px_24px_rgba(0,0,0,0.28)]">
                Email copied
              </div>
            </m.div>
          ) : null}
        </AnimatePresence>
      </>
    </LazyMotion>
  )
}
